import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import "@/App.css";
import * as serviceWorkerRegistration from "@/serviceWorkerRegistration";
import { InstallPrompt } from "@/components/InstallPrompt";
import { UpdateBanner } from "@/components/UpdateBanner";
import { ModeProvider } from "@/context/ModeContext";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Appointments from "@/pages/Appointments";
import ReferralDoctors from "@/pages/ReferralDoctors";
import ReferralDoctorSummary from "@/pages/ReferralDoctorSummary";
import FacilityCharges from "@/pages/FacilityCharges";
import Settings from "@/pages/Settings";
import AppointmentDetail from "@/pages/AppointmentDetail";
import FacilityChargeDetail from "@/pages/FacilityChargeDetail";
import Clinics from "@/pages/Clinics";
import ClinicDetail from "@/pages/ClinicDetail";
import StaffDetail from "@/pages/StaffDetail";
import Invoices from "@/pages/Invoices";
import InvoiceGeneration from "@/pages/InvoiceGeneration";
import InvoiceDetail from "@/pages/InvoiceDetail";
import ResetPassword from "@/pages/ResetPassword";
import PublicBooking from "@/pages/PublicBooking";
import AdminSubscriptions from "@/pages/AdminSubscriptions";
import Reports from "@/pages/Reports";
import CustomForms from "@/pages/CustomForms";
import BackButtonHandler from "@/components/BackButtonHandler";
import { InitialLoadScreen } from "@/components/InitialLoadScreen";
import { useUserRole } from "@/hooks/useUserRole";
import { useSyncState } from "@/hooks/useSyncState";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { initErrorTracking } from "@/services/errorTracking";
import { API } from "@/config";

// New Hooks
import { useAuth } from "@/hooks/useAuth";
import { useAxiosSetup } from "@/hooks/useAxiosSetup";
import FinancialRoute from "@/components/FinancialRoute";

// Initialize error tracking on app load
initErrorTracking();

function ProtectedRoute({ children, isAuthenticated }) {
  return isAuthenticated ? children : <Navigate to="/login" />;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node,
  isAuthenticated: PropTypes.bool
};

export function HomeRedirect({ token, onLogout }) {
  const { isReceptionistOnly, isLoading } = useUserRole();
  const { isInitialSyncComplete } = useSyncState();

  // Show loading screen during initial sync or while checking user role
  if (isLoading || !isInitialSyncComplete) {
    return <InitialLoadScreen />;
  }

  if (isReceptionistOnly) {
    return <Navigate to="/clinics" replace />;
  }

  return <Dashboard token={token} onLogout={onLogout} />;
}

HomeRedirect.propTypes = {
  token: PropTypes.string,
  onLogout: PropTypes.func
};

function App() {
  const { token, userId, login, logout, isAuthenticated } = useAuth();
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);

  // Setup Axios Interceptors
  useAxiosSetup({ login, logout, userId, token });

  // Proactive refresh on mount
  useEffect(() => {
    const restoreSession = async () => {
      // If we already have a token, we are good (though we might want to verify it, but let's assume valid for now)
      if (token) {
        setIsAuthChecking(false);
        return;
      }

      try {
        const res = await axios.post(`${API}/auth/refresh`);
        if (res.data.token) {
          login(res.data.token, res.data.user_id);
        }
      } catch {
        // Ignore error, stay logged out
      } finally {
        setIsAuthChecking(false);
      }
    };
    restoreSession();
  }, []); // Run once on mount

  // Register service worker for PWA functionality
  useEffect(() => {
    serviceWorkerRegistration.register({
      onUpdate: (registration) => {
        // New content available - show update banner
        console.log('New content available, please refresh.');
        setSwRegistration(registration);
        setUpdateAvailable(true);
      },
      onSuccess: () => {
        console.log('App ready for offline use.');
      },
    });
  }, []);

  if (isAuthChecking) {
    return <div className="flex items-center justify-center h-screen bg-slate-50">Loading...</div>;
  }

  return (
    <ErrorBoundary>
      <div className="App">
        <ModeProvider>
          <BrowserRouter>
            <BackButtonHandler />
            <Routes>
              <Route
                path="/login"
                element={isAuthenticated ? <Navigate to="/" /> : <Login onLogin={login} />}
              />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/book/clinic/:clinicId" element={<PublicBooking />} />
              <Route path="/book/doctor/:doctorId" element={<PublicBooking />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <HomeRedirect token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patients"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Patients token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patients/:patientId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <PatientDetail token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Appointments token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments/:appointmentId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <AppointmentDetail token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/referrals"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <ReferralDoctors token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/referrals/:doctorId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <ReferralDoctorSummary token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/facility-charges"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <FinancialRoute>
                      <FacilityCharges token={token} onLogout={logout} />
                    </FinancialRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/facility-charges/:chargeId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <FinancialRoute>
                      <FacilityChargeDetail token={token} onLogout={logout} />
                    </FinancialRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/custom-forms"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <CustomForms token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Settings token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clinics"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Clinics token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clinics/:clinicId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <ClinicDetail token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clinics/:clinicId/staff/:memberId"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <StaffDetail token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <Invoices token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices/new"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <InvoiceGeneration token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices/:id"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <InvoiceDetail token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/super-admin-subs"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <AdminSubscriptions token={token} onLogout={logout} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <FinancialRoute>
                      <Reports onLogout={logout} />
                    </FinancialRoute>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" />
          <InstallPrompt />
          {updateAvailable && <UpdateBanner registration={swRegistration} />}
        </ModeProvider>
      </div>
    </ErrorBoundary>
  );
}

export default App;